import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {getIncome, getExpense, getInvoicesSummary, getProfil} from "../../../api";
import {logError, logInfo} from "../../../logging/loggingService";
import {useAuth} from "../../auth/AuthContext";
import {hasAccountingAccess} from "../../../subscription";
import {
    extractArray,
    isPaid,
    monthKey,
    parseDate,
    safeExpenseAmount,
    safeIncomeAmount,
    safeInvoiceTotal,
    sum,
} from "./dashboardUtils";

export default function useDashboardData() {
    const [invoices, setInvoices] = useState([]);
    const [incomes, setIncomes] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [toast, setToast] = useState({open: false, msg: "", severity: "success"});
    const [profileChecked, setProfileChecked] = useState(false);
    const [hasProfile, setHasProfile] = useState(false);
    const [monthFilter, setMonthFilter] = useState("current");
    const [tourOpen, setTourOpen] = useState(false);
    const [tourCompleted, setTourCompleted] = useState(() => {
        try {
            return localStorage.getItem("dashboardTourCompleted") === "true";
        } catch (error) {
            return false;
        }
    });
    const [tourDismissed, setTourDismissed] = useState(() => {
        try {
            return localStorage.getItem("dashboardTourDismissed") === "true";
        } catch (error) {
            return false;
        }
    });

    const today = useMemo(() => new Date(), []);
    const {user} = useAuth();
    const hasAccounting = hasAccountingAccess(user?.paymentPlan);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;

        const verifyProfile = async () => {
            let profileExists = false;
            try {
                const res = await getProfil();
                if (cancelled) {
                    return;
                }
                profileExists = Boolean(res?.data?.id);
                setHasProfile(profileExists);
                if (!profileExists) {
                    navigate("/profile", {replace: true});
                }
            } catch (error) {
                if (!cancelled) {
                    logError("Profilprüfung fehlgeschlagen", error, {component: "Dashboard"});
                    setHasProfile(false);
                    setErr("Profil konnte nicht geladen werden.");
                }
            } finally {
                if (!cancelled) {
                    setProfileChecked(true);
                    if (!profileExists) {
                        setLoading(false);
                    }
                }
            }
        };

        verifyProfile();

        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const fetchAll = useCallback(() => {
        setLoading(true);
        setErr(null);
        const incomePromise = hasAccounting
            ? getIncome().catch(() => ({data: []}))
            : Promise.resolve({data: []});
        const expensePromise = hasAccounting
            ? getExpense().catch(() => ({data: []}))
            : Promise.resolve({data: []});

        Promise.all([
            getInvoicesSummary().catch(() => ({data: []})),
            incomePromise,
            expensePromise,
        ])
            .then(([invRes, incRes, expRes]) => {
                setInvoices(extractArray(invRes));
                setIncomes(hasAccounting ? extractArray(incRes) : []);
                setExpenses(hasAccounting ? extractArray(expRes) : []);
            })
            .catch((error) => {
                logError("Dashboard-Ladevorgang fehlgeschlagen", error, {component: "Dashboard"});
                setErr("Daten konnten nicht geladen werden.");
            })
            .finally(() => setLoading(false));
    }, [hasAccounting]);

    useEffect(() => {
        if (!profileChecked || !hasProfile) {
            return;
        }
        fetchAll();
    }, [fetchAll, hasProfile, profileChecked]);

    useEffect(() => {
        if (!tourCompleted && !tourDismissed && profileChecked && hasProfile && !tourOpen) {
            setTourOpen(true);
        }
    }, [tourCompleted, tourDismissed, profileChecked, hasProfile, tourOpen]);

    const tourSteps = useMemo(
        () => [
            {
                title: "Kennzahlen im Blick",
                description:
                    "Die oberen Kacheln zeigen offene und überfällige Rechnungen sowie – je nach Tarif – Ihre Einnahmen und Ausgaben.",
                tip: "Nutzen Sie den Zeitraumfilter auf der rechten Seite, um den Vergleich monatlich oder übergreifend zu betrachten.",
            },
            {
                title: "Fällige Rechnungen",
                description:
                    "Im Abschnitt \"Offene Rechnungen\" finden Sie die nächsten Fälligkeiten inklusive direkter Links zur Bearbeitung.",
                tip: "Klicken Sie auf eine Zeile, um direkt in die Rechnungsansicht zu springen.",
            },
            {
                title: "Cashflow analysieren",
                description: hasAccounting
                    ? "Die Cashflow-Box zeigt, wie sich Einnahmen und Ausgaben in den letzten sechs Monaten entwickelt haben."
                    : "Mit dem Buchhaltungstarif sehen Sie hier Ihren Cashflow der letzten Monate.",
                tip: hasAccounting
                    ? "Bewegen Sie den Mauszeiger über die Linien, um detaillierte Werte anzuzeigen."
                    : "Aktivieren Sie den Buchhaltungstarif, um diese Ansicht freizuschalten.",
            },
            {
                title: "Hilfe & FAQ",
                description:
                    "Unterhalb des Dashboards finden Sie die neue FAQ-Suche – geben Sie Stichwörter ein, um sofort Antworten zu erhalten.",
                tip: "Die Kontext-Tooltips in Karten und Tabellen erklären Funktionen direkt im Arbeitsfluss.",
            },
        ],
        [hasAccounting]
    );

    const startTour = useCallback(() => {
        logInfo("Dashboard guided tour triggered", {component: "Dashboard"});
        try {
            localStorage.removeItem("dashboardTourDismissed");
        } catch (error) {
            /* ignore */
        }
        setTourDismissed(false);
        setTourOpen(true);
    }, []);

    const handleTourClose = useCallback(() => {
        setTourOpen(false);
        setTourDismissed(true);
        try {
            localStorage.setItem("dashboardTourDismissed", "true");
        } catch (error) {
            /* ignore */
        }
    }, []);

    const handleTourComplete = useCallback(() => {
        setTourCompleted(true);
        try {
            localStorage.setItem("dashboardTourCompleted", "true");
        } catch (error) {
            /* ignore storage errors */
        }
        setTourOpen(false);
        setToast({open: true, msg: "Tour abgeschlossen!", severity: "success"});
    }, []);

    const openInvoices = useMemo(() => invoices.filter((inv) => !isPaid(inv)), [invoices]);
    const overdueInvoices = useMemo(
        () =>
            openInvoices.filter((inv) => {
                const due = parseDate(inv?.dueDate);
                return due && due < today;
            }),
        [openInvoices, today]
    );

    const totalOpen = useMemo(() => sum(openInvoices.map(safeInvoiceTotal)), [openInvoices]);
    const totalOverdue = useMemo(() => sum(overdueInvoices.map(safeInvoiceTotal)), [overdueInvoices]);

    const currentMonthKey = monthKey(today);
    const selectedYear = useMemo(() => {
        if (monthFilter.startsWith("year-")) {
            const yearValue = Number(monthFilter.slice(5));
            return Number.isFinite(yearValue) ? yearValue : null;
        }
        return null;
    }, [monthFilter]);
    const periodLabel = useMemo(() => {
        if (monthFilter === "current") return currentMonthKey;
        if (monthFilter === "all") return "Gesamt";
        if (selectedYear) return String(selectedYear);
        return "Zeitraum";
    }, [monthFilter, currentMonthKey, selectedYear]);

    const matchesSelectedPeriod = useCallback(
        (date) => {
            if (monthFilter === "all") return true;
            if (monthFilter === "current") return monthKey(date) === currentMonthKey;
            if (selectedYear) {
                const parsed = parseDate(date);
                return parsed && parsed.getFullYear() === selectedYear;
            }
            return true;
        },
        [monthFilter, currentMonthKey, selectedYear]
    );

    const availableYears = useMemo(() => {
        const years = new Set();
        const collectYear = (date) => {
            const parsed = parseDate(date);
            if (parsed) {
                years.add(parsed.getFullYear());
            }
        };
        invoices.forEach((inv) => collectYear(inv?.invoiceDate ?? inv?.paymentDate));
        incomes.forEach((inc) => collectYear(inc?.uploadDate ?? inc?.date));
        expenses.forEach((exp) => collectYear(exp?.invoiceDate ?? exp?.uploadDate));
        return Array.from(years).sort((a, b) => b - a);
    }, [invoices, incomes, expenses]);

    const yearOptions = useMemo(() => {
        if (!selectedYear || availableYears.includes(selectedYear)) {
            return availableYears;
        }
        return [selectedYear, ...availableYears].sort((a, b) => b - a);
    }, [availableYears, selectedYear]);

    const incomeThisMonth = useMemo(() => {
        const incomeSum = incomes
            .filter((income) => matchesSelectedPeriod(income?.uploadDate ?? income?.date))
            .reduce((acc, income) => acc + safeIncomeAmount(income), 0);

        const invoiceSum = invoices
            .filter((invoice) => isPaid(invoice) && matchesSelectedPeriod(invoice?.invoiceDate))
            .reduce((acc, invoice) => acc + safeInvoiceTotal(invoice), 0);

        return incomeSum + invoiceSum;
    }, [incomes, invoices, matchesSelectedPeriod]);

    const expenseThisMonth = useMemo(
        () =>
            sum(
                expenses
                    .filter((expense) => matchesSelectedPeriod(expense?.invoiceDate ?? expense?.uploadDate))
                    .map(safeExpenseAmount)
            ),
        [expenses, matchesSelectedPeriod]
    );

    const last6 = useMemo(() => {
        const out = [];
        const date = new Date(today.getFullYear(), today.getMonth(), 1);
        for (let i = 5; i >= 0; i--) {
            const dt = new Date(date.getFullYear(), date.getMonth() - i, 1);
            out.push(monthKey(dt));
        }
        return out;
    }, [today]);

    const series = useCallback((arr, keyFn, valFn) => {
        const map = new Map(last6.map((key) => [key, 0]));
        arr.forEach((entry) => {
            const key = keyFn(entry);
            if (map.has(key)) map.set(key, map.get(key) + valFn(entry));
        });
        return last6.map((key) => map.get(key));
    }, [last6]);

    const incomeSeries = useMemo(() => {
        const inc = series(
            incomes,
            (income) => monthKey(income?.uploadDate ?? income?.date),
            safeIncomeAmount
        );

        const inv = series(
            invoices.filter(isPaid),
            (invoice) => monthKey(invoice?.paymentDate ?? invoice?.invoiceDate),
            safeInvoiceTotal
        );

        return inc.map((value, idx) => (value || 0) + (inv[idx] || 0));
    }, [incomes, invoices, series]);

    const expenseSeries = useMemo(
        () => series(expenses, (expense) => monthKey(expense?.invoiceDate ?? expense?.uploadDate), safeExpenseAmount),
        [expenses, series]
    );
    const cashflowSeries = useMemo(
        () => last6.map((_, idx) => (incomeSeries[idx] || 0) - (expenseSeries[idx] || 0)),
        [last6, incomeSeries, expenseSeries]
    );

    const nextDue = useMemo(
        () =>
            openInvoices
                .map((inv) => ({...inv, _due: parseDate(inv?.dueDate)}))
                .filter((inv) => inv._due)
                .sort((a, b) => a._due - b._due)
                .slice(0, 5),
        [openInvoices]
    );

    const chartData = useMemo(() => {
        const invoiceCount = invoices.filter((invoice) => matchesSelectedPeriod(invoice?.invoiceDate)).length;
        const data = [{label: "Rechnungen", value: invoiceCount, color: "#4caf50"}];
        if (hasAccounting) {
            const incomeCount = incomes.filter((income) => matchesSelectedPeriod(income?.uploadDate ?? income?.date)).length;
            const expenseCount = expenses.filter((expense) => matchesSelectedPeriod(expense?.invoiceDate ?? expense?.uploadDate)).length;
            data.push(
                {label: "Einnahmen", value: incomeCount, color: "#2196f3"},
                {label: "Ausgaben", value: expenseCount, color: "#f44336"}
            );
        }
        return data;
    }, [invoices, incomes, expenses, hasAccounting, matchesSelectedPeriod]);

    const handleRefresh = useCallback(() => {
        fetchAll();
        setToast({open: true, msg: "Aktualisiert.", severity: "success"});
    }, [fetchAll]);

    return {
        hasAccounting,
        loading,
        err,
        toast,
        setToast,
        monthFilter,
        setMonthFilter,
        yearOptions,
        periodLabel,
        openInvoices,
        overdueInvoices,
        totalOpen,
        totalOverdue,
        incomeThisMonth,
        expenseThisMonth,
        incomeSeries,
        expenseSeries,
        cashflowSeries,
        last6,
        nextDue,
        chartData,
        invoices,
        incomes,
        expenses,
        handleRefresh,
        tourOpen,
        tourSteps,
        startTour,
        handleTourClose,
        handleTourComplete,
    };
}
